import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowRight, House, Key, ChartLineUp } from "@phosphor-icons/react";
import SiteNav from "@/components/SiteNav";
import PropertyCard from "@/components/PropertyCard";
import Seo from "@/lib/seo";
import api from "@/lib/apiClient";
import { formatPrice } from "@/lib/constants";

export default function NeighborhoodDetail() {
  const { slug } = useParams();
  const [hood, setHood] = useState(null);
  const [listings, setListings] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let on = true;
    setHood(null);
    setListings([]);
    setNotFound(false);
    api.get(`/neighborhoods/${slug}`)
      .then(({ data }) => {
        if (!on) return;
        setHood(data);
        return api.get("/properties", { params: { region: data.region, limit: 6 } });
      })
      .then((r) => { if (on && r) setListings(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (on) setNotFound(true); });
    return () => { on = false; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-background min-h-screen">
        <SiteNav />
        <div className="max-w-3xl mx-auto px-6 pt-40 text-center">
          <p className="text-white/60">Neighborhood not found.</p>
          <Link to="/neighborhoods" className="text-brand text-sm mt-3 inline-block">All neighborhoods</Link>
        </div>
      </div>
    );
  }

  const jsonLd = hood ? {
    "@context": "https://schema.org",
    "@type": "Place",
    name: `${hood.name}, NY`,
    description: hood.blurb,
    url: `https://whyteglove.vip/neighborhoods/${hood.slug}`,
  } : null;

  return (
    <div className="bg-background min-h-screen">
      {hood && (
        <Seo
          title={`${hood.name} Homes & Market Guide`}
          description={`${hood.blurb} Live listings, pricing and local guidance from Lamar A. Whyte, REALTOR®.`}
          path={`/neighborhoods/${hood.slug}`}
          jsonLd={jsonLd}
        />
      )}
      <SiteNav />
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-16">
        {!hood ? (
          <div className="py-24 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>
        ) : (
          <>
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3 flex items-center gap-2">
              <MapPin size={14} weight="duotone" /> Neighborhood guide
            </p>
            <h1 className="text-4xl md:text-6xl text-white">{hood.name}</h1>
            <p className="text-white/60 mt-4 max-w-2xl font-light leading-relaxed">{hood.blurb}</p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl" data-testid="hood-stats">
              <div className="rounded-sm border border-white/5 bg-[#101116] p-4">
                <p className="text-2xl text-white flex items-center gap-2"><House size={20} className="text-brand" /> {hood.active_sale}</p>
                <p className="text-[11px] uppercase tracking-wider text-white/40 mt-1">For sale now</p>
              </div>
              <div className="rounded-sm border border-white/5 bg-[#101116] p-4">
                <p className="text-2xl text-white flex items-center gap-2"><Key size={20} className="text-brand" /> {hood.active_rent}</p>
                <p className="text-[11px] uppercase tracking-wider text-white/40 mt-1">For rent now</p>
              </div>
              {hood.median_active_price ? (
                <div className="rounded-sm border border-white/5 bg-[#101116] p-4">
                  <p className="text-2xl text-white flex items-center gap-2"><ChartLineUp size={20} className="text-brand" /> {formatPrice(hood.median_active_price)}</p>
                  <p className="text-[11px] uppercase tracking-wider text-white/40 mt-1">Median asking</p>
                </div>
              ) : null}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/listings?region=${encodeURIComponent(hood.region)}`}
                data-testid="hood-browse-btn"
                className="inline-flex items-center gap-2 bg-brand text-black px-6 py-3 rounded-sm text-sm uppercase tracking-wider font-medium hover:bg-brand-hover"
              >
                Browse {hood.name} homes <ArrowRight size={15} weight="bold" />
              </Link>
              <Link
                to="/home-value"
                data-testid="hood-value-btn"
                className="inline-flex items-center gap-2 border border-brand/40 text-brand px-6 py-3 rounded-sm text-sm uppercase tracking-wider hover:bg-brand/10"
              >
                What's my {hood.name} home worth?
              </Link>
            </div>

            {listings.length > 0 && (
              <div className="mt-14">
                <h2 className="text-2xl text-white mb-6">Homes in and around {hood.name}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hood-listings">
                  {listings.map((p) => <PropertyCard key={p.id} property={p} />)}
                </div>
              </div>
            )}

            <div className="mt-16 rounded-sm border border-brand/25 bg-brand/5 p-8 text-center">
              <h2 className="text-2xl text-white">Thinking about {hood.name}?</h2>
              <p className="text-white/55 mt-2 font-light max-w-xl mx-auto">
                I'll give you the honest read — pricing, timing, and whether it fits your plan. No pressure.
              </p>
              <Link
                to="/book"
                data-testid="hood-book-btn"
                className="mt-5 inline-flex items-center gap-2 bg-brand text-black px-7 py-3 rounded-sm text-sm uppercase tracking-wider font-medium hover:bg-brand-hover"
              >
                Book a free call <ArrowRight size={15} weight="bold" />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
